import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
  InputBase,
  alpha,
} from '@mui/material';
import {
  PhoneAndroid as AndroidIcon,
  Warning as ForceIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import type { ActiveVersion } from '../../types/AppMobileTypes';

interface ActiveVersionCardProps {
  version: ActiveVersion | null;
  loading?: boolean;
  /**
   * Per le app che hanno anche la web app (YumaCALL, YumaWORK), il link UNICO da mandare a chi le installa: la pagina
   * `/scarica`, che sul telefono Android propone l'app o la web app e sull'iPhone la web app (20/10/2026). Lo stesso
   * del codice QR nelle pagine «App per il telefono» e «Le mie app» del gestionale.
   */
  paginaDaMandare?: string | null;
  /**
   * L'indirizzo stabile dell'APK (`indirizzoApk`, 30/10/2026): un nostro indirizzo che porta sempre all'ultima versione.
   * Si mostra questo, mai l'`apkDownloadUrl` di Storage, che porta il nome di Firebase e cambia a ogni rilascio.
   */
  linkApk: string;
}

function formatDate(timestamp: any): string {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBytes(bytes: number): string {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const ActiveVersionCard: React.FC<ActiveVersionCardProps> = ({ version, loading, paginaDaMandare, linkApk }) => {
  const [copied, setCopied] = useState(false);
  const [paginaCopiata, setPaginaCopiata] = useState(false);
  const copiaPagina = useCallback(async () => {
    if (!paginaDaMandare) return;
    try {
      await navigator.clipboard.writeText(paginaDaMandare);
      setPaginaCopiata(true);
      setTimeout(() => setPaginaCopiata(false), 2000);
    } catch (e) {
      console.error('[app mobile] copia della pagina da mandare non riuscita', e);
    }
  }, [paginaDaMandare]);

  const handleCopy = useCallback(async () => {
    if (!version?.apkDownloadUrl) return;
    try {
      await navigator.clipboard.writeText(linkApk);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = linkApk;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [version?.apkDownloadUrl, linkApk]);

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 3, py: 2, bgcolor: alpha('#1b1b1b', 0.03) }}>
          <Skeleton variant="text" width={180} height={28} />
        </Box>
        <CardContent sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
            <Skeleton variant="text" width={60} height={48} />
            <Skeleton variant="text" width={100} />
            <Skeleton variant="text" width={80} />
          </Box>
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (!version) {
    return (
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: alpha('#1b1b1b', 0.03),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <AndroidIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ letterSpacing: '0.02em' }}>
            Versione Attiva
          </Typography>
        </Box>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: alpha('#78909c', 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <AndroidIcon sx={{ fontSize: 28, color: 'grey.400' }} />
          </Box>
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Nessuna versione pubblicata
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Carica un APK per pubblicare la prima versione
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const metadataItems = [
    { label: 'Codice', value: version.versionCode, mono: true },
    { label: 'Dimensione', value: formatBytes(version.apkSizeBytes) },
    { label: 'Pacchetto', value: version.packageName, mono: true, small: true },
    { label: 'Pubblicato il', value: formatDate(version.published_at) },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: '#4caf50',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: alpha('#4caf50', 0.06),
          borderBottom: '1px solid',
          borderColor: alpha('#4caf50', 0.2),
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: '#4caf50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AndroidIcon sx={{ fontSize: 18, color: 'white' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ flex: 1, letterSpacing: '0.02em' }}>
          Versione Attiva
        </Typography>
        {version.forceUpdate && (
          <Chip
            icon={<ForceIcon sx={{ fontSize: '16px !important' }} />}
            label="Aggiornamento obbligatorio"
            color="warning"
            size="small"
            sx={{ height: 24, fontSize: 11, fontWeight: 600 }}
          />
        )}
      </Box>

      <CardContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, mb: 2 }}>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              Versione
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: '#4caf50',
                lineHeight: 1.1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {version.versionName}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
              flex: 1,
              pt: 0.25,
            }}
          >
            {metadataItems.map((item) => (
              <Box key={item.label}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{
                    fontFamily: item.mono ? 'monospace' : 'inherit',
                    fontSize: item.small ? 12 : 13,
                    mt: 0.25,
                  }}
                >
                  {item.value || '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {paginaDaMandare && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Link da mandare, per Android e iPhone (sceglie fra l’app e la web app)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <InputBase value={paginaDaMandare} readOnly fullWidth sx={{ px: 1.5, py: 0.75, fontSize: 12, color: 'text.secondary' }} onClick={(e) => (e.target as HTMLInputElement).select()} />
              <Tooltip title={paginaCopiata ? 'Copiato' : 'Copia il link'} arrow>
                <IconButton onClick={copiaPagina} size="small" sx={{ borderRadius: 0, borderLeft: '1px solid', borderColor: 'divider', px: 1.5, py: 0.75, color: paginaCopiata ? '#4caf50' : 'action.active' }}>
                  {paginaCopiata ? <CheckIcon sx={{ fontSize: 18 }} /> : <CopyIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {version.apkDownloadUrl && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}
              >
                Link dell’APK (non cambia con gli aggiornamenti)
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: alpha('#000', 0.02),
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: alpha('#4caf50', 0.5) },
              }}
            >
              <InputBase
                value={linkApk}
                readOnly
                fullWidth
                sx={{
                  px: 1.5,
                  py: 0.75,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  '& input': { cursor: 'text' },
                }}
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Tooltip title={copied ? 'Copiato!' : 'Copia link'} arrow>
                <IconButton
                  onClick={handleCopy}
                  size="small"
                  sx={{
                    borderRadius: 0,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    px: 1.5,
                    py: 0.75,
                    color: copied ? '#4caf50' : 'action.active',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: alpha('#4caf50', 0.06) },
                  }}
                >
                  {copied ? <CheckIcon sx={{ fontSize: 18 }} /> : <CopyIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {version.releaseNotes && version.releaseNotes.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: alpha('#000', 0.02),
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha('#000', 0.06),
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                display: 'block',
                mb: 1,
              }}
            >
              Note di rilascio
            </Typography>
            {version.releaseNotes.map((note, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{
                  fontWeight: note.startsWith('---') ? 600 : 400,
                  color: note.startsWith('---') ? 'text.primary' : 'text.secondary',
                  mt: note.startsWith('---') && i > 0 ? 1 : 0,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {note}
              </Typography>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
